import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { VINYL_COLORS } from '../data/constants';
import './ColorAudit.css';
import databaseService from '../services/databaseService';
import etsyDataService from '../services/etsyDataService';

import logger from '../utils/logger.js';
// Create dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: '#b0b0b0',
    },
  },
});

const ColorAudit = () => {
  const [loading, setLoading] = useState(false);
  const [auditResults, setAuditResults] = useState([]);
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    currentListing: '',
  });
  const [summary, setSummary] = useState({
    total: 0,
    compliant: 0,
    nonCompliant: 0,
    missingColors: 0,
    extraColors: 0,
  });
  const [selectedListing, setSelectedListing] = useState(null);
  const [etsyUpdateModal, setEtsyUpdateModal] = useState({ isOpen: false, data: null, type: '' });

  // State for enabled colors (all enabled by default)
  const [enabledColors, setEnabledColors] = useState(
    Object.keys(VINYL_COLORS).reduce((acc, colorName) => {
      acc[colorName] = true;
      return acc;
    }, {})
  );

  // Define the standard color order for listings
  const colorOrder = [
    'BLACK',
    'WHITE',
    'GRAY',
    'RED',
    'ORANGE',
    'YELLOW',
    'DOZER_YELLOW',
    'GREEN',
    'FOREST_GREEN',
    'ICE_BLUE',
    'SKY_BLUE',
    'BLUE',
    'PURPLE',
    'VIOLET',
    'PINK',
    'NUT_BROWN',
    'BROWN',
    'SILVER_METALLIC',
    'GOLD_METALLIC',
    'COPPER_METALLIC',
    'GRAY_HOLOGRAPHIC',
  ];

  // Map color names to display names
  const getDisplayName = (name) => {
    switch (name) {
      case 'BLACK':
        return 'Black - Matte';
      case 'WHITE':
        return 'White - Matte';
      case 'SILVER_METALLIC':
        return 'Silver';
      case 'GOLD_METALLIC':
        return 'Gold';
      case 'COPPER_METALLIC':
        return 'Copper';
      case 'GRAY_HOLOGRAPHIC':
        return 'Gray Holographic';
      default:
        return name.replace(/_/g, ' ');
    }
  };

  const handleColorToggle = (colorName) => {
    setEnabledColors((prev) => ({
      ...prev,
      [colorName]: !prev[colorName],
    }));
  };

  const assignDefaultColors = () => {
    // Enable all colors from the global color list
    const allColorsEnabled = {};
    colorOrder.forEach((colorName) => {
      allColorsEnabled[colorName] = true;
    });
    setEnabledColors(allColorsEnabled);
  };

  const handleListingColorToggle = (colorName) => {
    if (selectedListing) {
      setSelectedListing((prev) => ({
        ...prev,
        enabledColors: {
          ...prev.enabledColors,
          [colorName]: !prev.enabledColors[colorName],
        },
      }));
    }
  };

  const assignDefaultColorsToSelected = () => {
    if (selectedListing) {
      const allColorsEnabled = {};
      colorOrder.forEach((colorName) => {
        allColorsEnabled[colorName] = true;
      });
      setSelectedListing((prev) => ({
        ...prev,
        enabledColors: allColorsEnabled,
      }));
    }
  };

  const updateListingColors = (listing) => {
    // Get enabled colors
    const enabledColorNames = Object.keys(listing.enabledColors).filter(
      (color) => listing.enabledColors[color]
    );

    // Convert color names to Etsy format
    const etsyColorValues = enabledColorNames.map((color) => {
      const displayName = getDisplayName(color);
      return displayName
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    });
    // We need to get real value IDs from the property discovery
    // For now, we'll need to implement property discovery to get real IDs
    const etsyColorValueIds = []; // TODO: Get real value IDs from property discovery

    // Show approval modal
    setEtsyUpdateModal({
      isOpen: true,
      type: 'colors',
      data: {
        shopId: '6798191', // Your shop ID
        listingId: listing.listingId,
        propertyId: 200, // Color property ID
        valueIds: etsyColorValueIds,
        values: etsyColorValues,
        enabledColors: enabledColorNames,
        displayNames: enabledColorNames.map((color) => getDisplayName(color)),
      },
    });
  };

  const importAllListingData = async () => {
    setLoading(true);
    setProgress({ current: 0, total: 0, currentListing: '' });

    try {
      logger.log('📥 Starting import of all listing data...');

      // Fetch all shop listings
      const shopData = await etsyDataService.fetchAllShopListings();
      const listings = shopData.results || [];
      logger.log(`📊 Found ${listings.length} listings to import`);

      setProgress({ current: 0, total: listings.length, currentListing: '' });

      let imported = 0;
      let skipped = 0;
      let errors = 0;

      // Get list of already imported listings
      const existingListings = await databaseService.getAllEtsyListingIds();
      const existingIds = new Set(existingListings || []);
      logger.log(`📋 Found ${existingIds.size} already imported listings`);

      for (let i = 0; i < listings.length; i++) {
        const listing = listings[i];
        const listingId = listing.listing_id;

        setProgress({
          current: i + 1,
          total: listings.length,
          currentListing: `Importing: ${listing.title}`,
        });

        logger.log(
          `📦 Importing listing ${i + 1}/${listings.length}: ${listingId} - ${listing.title}`
        );

        try {
          // Check if data already exists
          if (existingIds.has(listingId)) {
            logger.log(`⏭️ Data already exists for listing ${listingId}, skipping`);
            skipped++;
            continue;
          }

          // Fetch and store complete listing data
          const completeData = await etsyDataService.fetchCompleteListingData(listingId);
          await etsyDataService.storeListingData(listingId, completeData);

          imported++;
          logger.log(`✅ Successfully imported listing ${listingId}`);
        } catch (error) {
          console.error(`❌ Failed to import listing ${listingId}:`, error);
          errors++;
        }

        // Small delay to avoid overwhelming the API
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      logger.log(`✅ Import complete: ${imported} imported, ${skipped} skipped, ${errors} errors`);
      alert(
        `Import complete!\n✅ ${imported} imported\n⏭️ ${skipped} skipped\n❌ ${errors} errors`
      );
    } catch (error) {
      console.error('❌ Error importing listing data:', error);
      alert(`Error importing listing data: ${error.message}`);
    } finally {
      setLoading(false);
      setProgress({ current: 0, total: 0, currentListing: '' });
    }
  };

  // Load existing audit results on component mount
  useEffect(() => {
    const loadAuditProgress = async () => {
      try {
        const savedProgress = await databaseService.getColorAuditProgress();
        if (savedProgress && savedProgress.audit_results) {
          const results = JSON.parse(savedProgress.audit_results);
          setAuditResults(results);

          const compliant = results.filter((r) => !r.hasIssues).length;
          const nonCompliant = results.filter((r) => r.hasIssues).length;
          const missingColors = results.reduce((sum, r) => sum + r.missingCoreColors.length, 0);
          const extraColors = results.reduce((sum, r) => sum + r.extraColors.length, 0);

          setSummary({
            total: results.length,
            compliant,
            nonCompliant,
            missingColors,
            extraColors,
          });
        }
      } catch (error) {
        console.error('Error loading audit progress:', error);
      }
    };

    loadAuditProgress();
  }, []);

  const runColorAudit = async () => {
    setLoading(true);
    setProgress({ current: 0, total: 0, currentListing: '' });
    setAuditResults([]);

    try {
      logger.log('🔍 Starting color audit...');

      // Fetch all shop listings
      const shopData = await etsyDataService.fetchAllShopListings();
      const listings = shopData.results || [];
      logger.log(`📊 Found ${listings.length} listings to audit`);

      setProgress({ current: 0, total: listings.length, currentListing: '' });

      // Load previous audit results from database
      const savedProgress = await databaseService.getColorAuditProgress();
      const previousResults = savedProgress?.audit_results
        ? JSON.parse(savedProgress.audit_results)
        : [];
      const processedIds = new Set(previousResults.map((r) => r.listingId));

      const results = [...previousResults];
      let compliant = previousResults.filter((r) => !r.hasIssues).length;
      let nonCompliant = previousResults.filter((r) => r.hasIssues).length;
      let missingColors = previousResults.reduce((sum, r) => sum + r.missingCoreColors.length, 0);
      let extraColors = previousResults.reduce((sum, r) => sum + r.extraColors.length, 0);

      for (let i = 0; i < listings.length; i++) {
        const listing = listings[i];
        const listingId = listing.listing_id;

        setProgress({
          current: i + 1,
          total: listings.length,
          currentListing: listing.title,
        });

        logger.log(
          `🔍 Auditing listing ${i + 1}/${listings.length}: ${listingId} - ${listing.title}`
        );

        // Skip if already processed
        if (processedIds.has(listingId)) {
          logger.log(`⏭️ Already processed listing ${listingId}, skipping`);
          continue;
        }

        // Get stored listing data from database
        const storedData = await databaseService.getEtsyListingData(listingId);

        if (!storedData) {
          logger.log(`⚠️ No stored data for listing ${listingId}, skipping`);
          continue;
        }

        const extractedVariations = JSON.parse(storedData.extracted_variations || '{}');
        const listingColors = extractedVariations.colors || [];

        // Map database color names to our global color constants
        const colorMapping = {
          Black: 'BLACK',
          White: 'WHITE',
          Gray: 'GRAY',
          Red: 'RED',
          Orange: 'ORANGE',
          Yellow: 'YELLOW',
          'Dozer Yellow': 'DOZER_YELLOW',
          Green: 'GREEN',
          'Forest Green': 'FOREST_GREEN',
          'Ice Blue': 'ICE_BLUE',
          'Sky Blue': 'SKY_BLUE',
          Blue: 'BLUE',
          Purple: 'PURPLE',
          Violet: 'VIOLET',
          Pink: 'PINK',
          Silver: 'SILVER_METALLIC',
          Gold: 'GOLD_METALLIC',
          Copper: 'COPPER_METALLIC',
          'Nut Brown': 'NUT_BROWN',
          Brown: 'BROWN',
          'Gray Holographic': 'GRAY_HOLOGRAPHIC',
        };

        // Convert listing colors to our format
        const mappedColors = listingColors
          .map((colorName) => colorMapping[colorName])
          .filter(Boolean);

        // Check for issues
        const missingCoreColors = colorOrder.filter((color) => !mappedColors.includes(color));
        const listingExtraColors = mappedColors.filter((color) => !colorOrder.includes(color));
        const hasIssues = missingCoreColors.length > 0 || listingExtraColors.length > 0;

        if (hasIssues) {
          nonCompliant++;
          missingColors += missingCoreColors.length;
          extraColors += listingExtraColors.length;
        } else {
          compliant++;
        }

        // Create enabled colors state for this listing
        const listingEnabledColors = {};
        colorOrder.forEach((colorName) => {
          listingEnabledColors[colorName] = mappedColors.includes(colorName);
        });

        const result = {
          listingId,
          title: listing.title,
          currentColors: listingColors,
          mappedColors,
          missingCoreColors,
          extraColors: listingExtraColors,
          hasIssues,
          enabledColors: listingEnabledColors,
          etsyUrl: `https://www.etsy.com/listing/${listingId}`,
        };

        results.push(result);

        // Save progress to database after each listing
        await databaseService.saveColorAuditProgress(results);

        // Small delay to avoid overwhelming the UI
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // Filter to only show non-compliant listings and sort by title
      const nonCompliantResults = results.filter((result) => result.hasIssues);
      nonCompliantResults.sort((a, b) => a.title.localeCompare(b.title));

      setAuditResults(nonCompliantResults);
      setSummary({
        total: results.length,
        compliant,
        nonCompliant,
        missingColors,
        extraColors,
      });
      setProgress({ current: 0, total: 0, currentListing: '' });

      logger.log(`✅ Color audit complete: ${nonCompliant} non-compliant listings found`);
    } catch (error) {
      console.error('❌ Error running color audit:', error);
      alert(`Error running color audit: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container maxWidth={false} sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Color Audit Tool
        </Typography>

        {/* Summary Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h6">Audit Summary</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="secondary"
                onClick={async () => {
                  await databaseService.clearColorAuditProgress();
                  setAuditResults([]);
                  setSummary({
                    total: 0,
                    compliant: 0,
                    nonCompliant: 0,
                    missingColors: 0,
                    extraColors: 0,
                  });
                }}
                disabled={loading}
              >
                Clear Progress
              </Button>
              <Button
                variant="outlined"
                color="info"
                onClick={importAllListingData}
                disabled={loading}
              >
                Import All Data
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={runColorAudit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              >
                {loading ? 'Running Audit...' : 'Run Color Audit'}
              </Button>
            </Box>
          </Box>

          {/* Progress Bar */}
          {loading && progress.total > 0 && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Processing: {progress.current}/{progress.total} listings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round((progress.current / progress.total) * 100)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(progress.current / progress.total) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
              {progress.currentListing && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Current: {progress.currentListing}
                </Typography>
              )}
            </Box>
          )}

          {summary.total > 0 && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4">{summary.total}</Typography>
                    <Typography variant="body2">Total Listings</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4">{summary.compliant}</Typography>
                    <Typography variant="body2">Compliant</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4">{summary.nonCompliant}</Typography>
                    <Typography variant="body2">Non-Compliant</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Card sx={{ bgcolor: 'error.main', color: 'white' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4">{summary.missingColors}</Typography>
                    <Typography variant="body2">Missing Colors</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h4">{summary.extraColors}</Typography>
                    <Typography variant="body2">Extra Colors</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Paper>

        {/* Audit Results */}
        {auditResults.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Audit Results
            </Typography>

            <Grid container spacing={2}>
              {auditResults.map((result, _index) => (
                <Grid size={{ xs: 12 }} key={result.listingId}>
                  <Card
                    sx={{
                      border: result.hasIssues ? '2px solid #ff9800' : '2px solid #4caf50',
                      bgcolor: result.hasIssues
                        ? 'rgba(255, 152, 0, 0.1)'
                        : 'rgba(76, 175, 80, 0.1)',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 2,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ mb: 1 }}>
                            {result.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Listing ID: {result.listingId}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {result.hasIssues ? (
                            <Chip icon={<WarningIcon />} label="Issues Found" color="warning" />
                          ) : (
                            <Chip icon={<CheckCircleIcon />} label="Compliant" color="success" />
                          )}
                          <Tooltip title="Open in Etsy">
                            <IconButton
                              size="small"
                              onClick={() => window.open(result.etsyUrl, '_blank')}
                            >
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            Current Colors ({result.currentColors.length})
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {result.currentColors.map((color, idx) => (
                              <Chip
                                key={idx}
                                label={color}
                                size="small"
                                variant="outlined"
                                color={
                                  result.missingCoreColors.includes(color) ? 'error' : 'default'
                                }
                              />
                            ))}
                          </Box>
                        </Box>

                        {result.hasIssues && (
                          <>
                            {result.missingCoreColors.length > 0 && (
                              <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                                  Missing Core Colors ({result.missingCoreColors.length})
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {result.missingCoreColors.map((color, idx) => (
                                    <Chip
                                      key={idx}
                                      label={getDisplayName(color)}
                                      size="small"
                                      color="error"
                                      variant="filled"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {result.extraColors.length > 0 && (
                              <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Typography variant="subtitle2" color="warning" sx={{ mb: 1 }}>
                                  Extra Colors ({result.extraColors.length})
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {result.extraColors.map((color, idx) => (
                                    <Chip
                                      key={idx}
                                      label={color}
                                      size="small"
                                      color="warning"
                                      variant="filled"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </>
                        )}
                      </Box>

                      {/* Color Management Interface */}
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2 }}>
                          Color Management
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => assignDefaultColorsToSelected()}
                            sx={{ fontSize: '0.8rem' }}
                          >
                            Assign Default
                          </Button>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => updateListingColors(result)}
                            sx={{ fontSize: '0.8rem' }}
                          >
                            Update Etsy
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {colorOrder.map((colorName) => {
                            const colorHex = VINYL_COLORS[colorName];
                            const isEnabled = result.enabledColors[colorName];
                            const textColor = colorHex === '#ffffff' ? '#000000' : '#ffffff';

                            return (
                              <Box
                                key={colorName}
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  padding: '4px 12px',
                                  borderRadius: '16px',
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                  backgroundColor: isEnabled ? colorHex : '#333333',
                                  color: isEnabled ? textColor : '#888888',
                                  border: isEnabled
                                    ? `2px solid ${colorHex}`
                                    : '2px dashed #666666',
                                  '&:hover': {
                                    opacity: 0.8,
                                  },
                                }}
                                onClick={() => {
                                  const updatedResults = auditResults.map((r) =>
                                    r.listingId === result.listingId
                                      ? {
                                          ...r,
                                          enabledColors: {
                                            ...r.enabledColors,
                                            [colorName]: !r.enabledColors[colorName],
                                          },
                                        }
                                      : r
                                  );
                                  setAuditResults(updatedResults);
                                }}
                              >
                                {getDisplayName(colorName)}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Color Management Section (from ListingAdmin) */}
        <Accordion defaultExpanded sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Color Management</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', gap: 3 }}>
              {/* Left side - Color Management */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Color Management
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={assignDefaultColors}
                    sx={{
                      fontSize: '0.8rem',
                      px: 2,
                      py: 0.5,
                    }}
                  >
                    Assign Default
                  </Button>
                </Box>
              </Box>

              {/* Center - Available Colors */}
              <Box sx={{ flex: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Available Colors (Core 21)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {colorOrder.map((colorName) => {
                    const colorHex = VINYL_COLORS[colorName];
                    const isEnabled = enabledColors[colorName];
                    const textColor = colorHex === '#ffffff' ? '#000000' : '#ffffff';

                    return (
                      <Box
                        key={colorName}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          cursor: 'pointer',
                          backgroundColor: isEnabled ? colorHex : '#333333',
                          color: isEnabled ? textColor : '#888888',
                          border: isEnabled ? `2px solid ${colorHex}` : '2px dashed #666666',
                          '&:hover': {
                            opacity: 0.8,
                          },
                        }}
                        onClick={() => handleColorToggle(colorName)}
                      >
                        {getDisplayName(colorName)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Right side - Info */}
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                  Audit Info
                </Typography>
                <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    This tool identifies listings that don't conform to the core 21 color pattern.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    • Missing core colors are highlighted in red
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    • Extra colors are highlighted in orange
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Click the link icon to open listings in Etsy for manual fixes
                  </Typography>
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Container>

      {/* Etsy Update Approval Modal */}
      {etsyUpdateModal.isOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            p: 2,
          }}
          onClick={() => setEtsyUpdateModal({ isOpen: false, data: null, type: '' })}
        >
          <Paper
            sx={{
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflow: 'auto',
              p: 3,
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Typography variant="h6" color="warning.main">
                ⚠️ Etsy Update Approval Required
              </Typography>
              <IconButton
                onClick={() => setEtsyUpdateModal({ isOpen: false, data: null, type: '' })}
                sx={{ color: 'text.secondary' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Typography variant="body1" sx={{ mb: 3 }}>
              Please review the following changes before updating Etsy:
            </Typography>

            {etsyUpdateModal.type === 'colors' && etsyUpdateModal.data && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Colors to Update on Etsy:
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {etsyUpdateModal.data.displayNames.map((colorName, index) => (
                    <Chip
                      key={index}
                      label={colorName}
                      color="primary"
                      variant="outlined"
                      sx={{ fontSize: '0.8rem' }}
                    />
                  ))}
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>API Details:</strong>
                </Typography>

                <Box sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                    <strong>Shop ID:</strong> {etsyUpdateModal.data.shopId}
                    <br />
                    <strong>Listing ID:</strong> {etsyUpdateModal.data.listingId}
                    <br />
                    <strong>Property ID:</strong> {etsyUpdateModal.data.propertyId}
                    <br />
                    <strong>Etsy Values:</strong> {etsyUpdateModal.data.values.join(', ')}
                    <br />
                    <strong>Value IDs:</strong> {etsyUpdateModal.data.valueIds.join(', ')}
                  </Typography>
                </Box>

                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                  ⚠️ <strong>Note:</strong> This will replace ALL existing colors on the Etsy
                  listing with the selected colors above.
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => setEtsyUpdateModal({ isOpen: false, data: null, type: '' })}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={async () => {
                  try {
                    if (etsyUpdateModal.type === 'colors' && etsyUpdateModal.data) {
                      // Get the real OAuth token
                      const authToken = etsyDataService.getAuthToken();
                      if (!authToken) {
                        alert('❌ No OAuth token found. Please connect to Etsy first.');
                        return;
                      }

                      const result = await databaseService.updateEtsyListingProperties(
                        etsyUpdateModal.data.shopId,
                        etsyUpdateModal.data.listingId,
                        etsyUpdateModal.data.propertyId,
                        etsyUpdateModal.data.valueIds,
                        etsyUpdateModal.data.values,
                        authToken
                      );

                      if (result) {
                        alert('✅ Colors updated on Etsy successfully!');
                      } else {
                        alert('❌ Failed to update colors on Etsy.');
                      }
                    }

                    setEtsyUpdateModal({ isOpen: false, data: null, type: '' });
                  } catch (error) {
                    console.error('Error updating Etsy:', error);
                    alert(`❌ Error updating Etsy: ${error.message}`);
                  }
                }}
              >
                Confirm Update to Etsy
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </ThemeProvider>
  );
};

// ColorAudit has no props - it's a standalone page component
ColorAudit.propTypes = {};

export default ColorAudit;
